import React, { useState } from 'react';
import { Clock, User, Eye, ChevronDown, ChevronUp, RotateCcw, X, Code, GitBranch, Check } from 'lucide-react';
import Modal from './Modal';

const VersionCard = ({ version, onDiff, onRevert, onDelete, onFork, isLatest, isDeleted, isSelected, onToggleSelect }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showForkModal, setShowForkModal] = useState(false);
    const [forkName, setForkName] = useState('');

    // Format date nicely
    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown date';
        return new Date(dateString).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleForkClick = (e) => {
        e.stopPropagation();
        setForkName(`${version.change_summary || 'Contract'} v${version.version_number} (Fork)`);
        setShowForkModal(true);
    };

    const handleForkConfirm = () => {
        if (onFork && forkName.trim()) {
            onFork(version, forkName.trim());
        }
        setShowForkModal(false);
        setForkName('');
    };

    return (
        <div className={`
            relative transition-all rounded-xl border group
            ${isLatest
                ? 'border-blue-900 bg-[#252526] shadow-sm'
                : 'border-[#3c3c3c] bg-[#252526] hover:border-[#4c4c4c]'}
            ${isDeleted ? 'opacity-60 grayscale' : ''}
        `}>
            {/* Main Clickable Header Area */}
            <div
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-4 cursor-pointer select-none"
            >
                <div className="flex justify-between items-start gap-4">
                    {/* Left: Info */}
                    <div className="flex-1 min-w-0 flex items-start gap-3">
                        {/* Selection Checkbox */}
                        {onToggleSelect && (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleSelect(version);
                                }}
                                className={`mt-1 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${isSelected
                                    ? 'bg-blue-600 border-blue-600 text-white'
                                    : 'border-gray-500 hover:border-blue-400'
                                    }`}
                            >
                                {isSelected && <Check size={12} strokeWidth={3} />}
                            </div>
                        )}

                        <div>
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                <span className={`
                                inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-bold tracking-wide
                                ${isLatest
                                        ? 'bg-blue-900/40 text-blue-300'
                                        : 'bg-[#3c3c3c] text-gray-400'}
                            `}>
                                    v{version.version_number}
                                </span>

                                {isLatest && (
                                    <span className="text-[10px] uppercase font-bold text-blue-400 tracking-wider">
                                        Current
                                    </span>
                                )}

                                {version.change_type && version.change_type !== 'manual' && (
                                    <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded
                                    ${version.change_type === 'revert' ? 'bg-orange-900/30 text-orange-300' :
                                            'bg-purple-900/30 text-purple-300'}
                                `}>
                                        {version.change_type}
                                    </span>
                                )}

                                <span className="text-xs text-gray-500">•</span>
                                <span className="text-xs text-gray-400 truncate max-w-[150px]">
                                    {version.created_by || 'system'}
                                </span>
                            </div>

                            <h3 className="text-base font-semibold text-white leading-snug mb-1">
                                {version.change_summary || "No description provided"}
                            </h3>

                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                <Clock size={12} />
                                {formatDate(version.created_at)}
                            </div>
                        </div>
                    </div>

                    {/* Right: Actions & Expand Icon */}
                    <div className="flex flex-col items-end gap-2 shrink-0">
                        {/* Action Buttons Row */}
                        <div
                            className="flex items-center gap-1.5"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {!isLatest && onDiff && (
                                <button
                                    onClick={() => onDiff(version)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-blue-400 hover:bg-blue-900/40"
                                >
                                    <Eye size={13} />
                                    Compare
                                </button>
                            )}
                            {!isLatest && onRevert && (
                                <button
                                    onClick={() => onRevert(version)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-orange-400 hover:bg-orange-900/40"
                                >
                                    <RotateCcw size={13} />
                                    Revert
                                </button>
                            )}
                            {onFork && (
                                <button
                                    onClick={handleForkClick}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-purple-400 hover:bg-purple-900/40"
                                >
                                    <GitBranch size={13} />
                                    Fork
                                </button>
                            )}
                            {!isLatest && onDelete && !isDeleted && (
                                <button
                                    onClick={() => onDelete(version)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors bg-[#3c3c3c] border border-transparent text-red-400 hover:bg-red-900/40"
                                >
                                    <X size={13} />
                                    Delete
                                </button>
                            )}
                        </div>

                        <div className={`
                            mt-1 p-1 rounded-full text-gray-400 transition-transform duration-200 cursor-pointer hover:bg-[#333]
                            ${isExpanded ? 'rotate-180 bg-[#333]' : ''}
                        `}>
                            <ChevronDown size={18} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Row */}
            {version.semantic_summary && (
                <div className="mt-3 flex gap-3 text-xs font-medium opacity-80 px-4 pb-3">
                    {version.semantic_summary.type_renames > 0 && (
                        <span className="text-orange-400">{version.semantic_summary.type_renames} type changes</span>
                    )}
                    {version.semantic_summary.field_renames > 0 && (
                        <span className="text-blue-400">{version.semantic_summary.field_renames} renames</span>
                    )}
                    {version.semantic_summary.added_fields > 0 && (
                        <span className="text-green-400">+{version.semantic_summary.added_fields} adds</span>
                    )}
                    {version.semantic_summary.removed_fields > 0 && (
                        <span className="text-red-400">-{version.semantic_summary.removed_fields} dels</span>
                    )}
                </div>
            )}

            {/* Expandable Content Area */}
            {isExpanded && (
                <div className="border-t border-[#333] bg-[#1f1f1f] rounded-b-xl overflow-hidden animate-in slide-in-from-top-2 duration-200">
                    <div className="p-4">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-1">
                                <Code size={12} />
                                Schema Content
                            </span>
                            <button
                                className="text-xs text-blue-400 cursor-pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(JSON.stringify(version.schema_content, null, 2));
                                }}
                            >
                                Copy JSON
                            </button>
                        </div>
                        <div className="bg-[#1a1a1a] rounded-lg border border-[#333] p-3 overflow-x-auto max-h-64 shadow-inner">
                            <pre className="text-[11px] font-mono leading-relaxed text-gray-300">
                                {JSON.stringify(version.schema_content, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>
            )}

            {/* Fork from Version Modal */}
            <Modal
                isOpen={showForkModal}
                onClose={() => setShowForkModal(false)}
                title="Fork from Version"
                footer={
                    <>
                        <button
                            onClick={() => setShowForkModal(false)}
                            className="px-4 py-2 text-[#cccccc] hover:bg-[#3c3c3c] rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleForkConfirm}
                            disabled={!forkName.trim()}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Fork Contract
                        </button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-purple-900/20 rounded-lg border border-purple-800">
                        <GitBranch className="text-purple-400" size={20} />
                        <div>
                            <p className="text-sm font-medium text-purple-300">
                                Forking from Version {version.version_number}
                            </p>
                            <p className="text-xs text-purple-400 truncate max-w-[300px]">
                                {version.change_summary || "No description"}
                            </p>
                        </div>
                    </div>

                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-[#cccccc] mb-1">
                            New Contract Name
                        </label>
                        <input
                            type="text"
                            value={forkName}
                            onChange={(e) => setForkName(e.target.value)}
                            placeholder="e.g., Company 850 v2 (Fork from v3)"
                            className="w-full p-2 border border-[#3c3c3c] rounded-lg bg-[#252526] text-white focus:ring-2 focus:ring-purple-500 outline-none"
                            autoFocus
                        />
                    </div>

                    <div className="bg-[#1e1e1e] p-3 rounded-lg text-xs text-gray-400">
                        <p>
                            <strong>Note:</strong> This creates a completely new contract starting with this
                            version's schema. The new contract will have its own version history starting at v1.
                        </p>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default VersionCard;
